import React, {Component, lazy, Suspense} from 'react';
import {captureException} from '@sentry/react';
import {translateService} from '../../services/TranslateService';

class ModuleLoadError extends Error {
    constructor(readonly originalError: unknown) {
        super('Unable to load a reservation machine module');
    }
}

class ModuleLoadBoundary extends Component<{children: React.ReactNode}, {failed: boolean}> {
    state = {failed: false};

    static getDerivedStateFromError(error: unknown) {
        // Do not turn application errors into misleading network errors.
        if (!(error instanceof ModuleLoadError)) throw error;
        return {failed: true};
    }

    componentDidCatch(error: ModuleLoadError) {
        captureException(error.originalError);
    }

    render() {
        if (this.state.failed) {
            return <div role="alert" className="p-4 text-center">
                <p>{translateService.translate('deferredModule.failed')}</p>
                <button className="btn btn-primary" onClick={() => window.location.reload()}>
                    {translateService.translate('deferredModule.reload')}
                </button>
            </div>;
        }
        return this.props.children;
    }
}

/**
 * Declare at module scope; the import starts only when the feature is rendered.
 *
 * `minHeight` must match the height of whatever the fallback replaces, or the swap is a
 * layout shift. A number is px; pass a CSS string when the replaced element is sized in
 * viewport units — see DeferredMap, where the placeholder is `100vh`.
 */
export function deferComponent<P extends object>(
    load: () => Promise<{default: React.ComponentType<P>}>,
    minHeight: number | string = 280,
) {
    const LazyComponent = lazy(() => load().catch(error => {throw new ModuleLoadError(error);}));
    return function DeferredComponent(props: P) {
        return <ModuleLoadBoundary>
            <Suspense fallback={<div role="status" aria-live="polite" aria-busy="true"
                                    className="p-4 text-center" style={{minHeight}}>
                {translateService.translate('deferredModule.loading')}
            </div>}>
                <LazyComponent {...props}/>
            </Suspense>
        </ModuleLoadBoundary>;
    };
}
